// Per-user offline-login credential store. Each row holds the user's identity,
// a PBKDF2 PIN hash (+ salt), that user's refresh token, and the failed-attempt
// lockout state. Provisioned at online login; verified locally when offline.
// Pure crypto/lockout logic lives in offlineAuth.js (unit-tested separately).

import { getDb } from './db.js';
import { hashPin, verifyPin, generateSalt, nextLockState } from './offlineAuth.js';

const STORE = 'offlineCredentials';
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // matches the refresh token's 7-day life

/** Create/refresh a user's offline credential (called on a successful online login). */
export async function provisionCredential({
  userId, fullName, role, email, pin, refreshToken, jwt = '',
  now = Date.now(), ttlMs = DEFAULT_TTL_MS,
}) {
  const salt = generateSalt();
  const pinHash = await hashPin(pin, salt);
  const row = {
    // jwt = the last online access token; restored on offline login so queued orders
    // keep carrying the maker's email (its claims), even though it may be expired —
    // the sync re-auths via the refresh token.
    userId, fullName, role, email, salt, pinHash, refreshToken, jwt,
    provisionedAt: now, refreshExpiresAt: now + ttlMs,
    failedAttempts: 0, lockedUntil: 0,
  };
  const db = await getDb();
  await db.put(STORE, row);
  return row;
}

export async function getCredential(userId) {
  const db = await getDb();
  return (await db.get(STORE, userId)) || null;
}

/** Keep the stored access token fresh after a silent refresh (offline session continuity). */
export async function updateCredentialJwt(userId, jwt) {
  const db = await getDb();
  const c = await db.get(STORE, userId);
  if (!c) return;
  c.jwt = jwt;
  await db.put(STORE, c);
}

export async function getAllCredentials() {
  const db = await getDb();
  return await db.getAll(STORE);
}

/** Credentials still within their refresh-token life — the set allowed to log in offline. */
export async function getValidCredentials(now = Date.now()) {
  return (await getAllCredentials()).filter((c) => c.refreshExpiresAt > now);
}

export async function removeCredential(userId) {
  const db = await getDb();
  await db.delete(STORE, userId);
}

/** Drop credentials whose refresh token has expired — they can no longer sync. */
export async function purgeExpired(now = Date.now()) {
  const db = await getDb();
  const all = await db.getAll(STORE);
  for (const c of all) {
    if (c.refreshExpiresAt <= now) await db.delete(STORE, c.userId);
  }
}

/**
 * Verify an offline PIN attempt and update lockout state.
 * Returns one of:
 *   { ok: true, credential }
 *   { ok: false, reason: 'no_credential' | 'expired' | 'locked' | 'bad_pin', lockedUntil?, failedAttempts? }
 */
export async function verifyOfflinePin(userId, pin, now = Date.now()) {
  const c = await getCredential(userId);
  if (!c) return { ok: false, reason: 'no_credential' };
  if (c.refreshExpiresAt <= now) {
    await removeCredential(userId);
    return { ok: false, reason: 'expired' };
  }
  if (c.lockedUntil > now) {
    return { ok: false, reason: 'locked', lockedUntil: c.lockedUntil };
  }

  const match = await verifyPin(pin, c.salt, c.pinHash);
  const { state } = nextLockState(c, match, now);
  Object.assign(c, state);
  const db = await getDb();
  await db.put(STORE, c);

  if (match) return { ok: true, credential: c };
  return {
    ok: false,
    reason: c.lockedUntil > now ? 'locked' : 'bad_pin',
    failedAttempts: c.failedAttempts,
    lockedUntil: c.lockedUntil,
  };
}
